import { takeLatest, put, select } from 'redux-saga/effects';
import exerciseSlice from 'store/modules/exercise';
import uiSlice from 'store/modules/ui';
import exercise from 'api/graphql/exercise';
import { createSnack, getUserFriendlyError } from 'common/utils/general';
import i18n from 'i18n';

/**
 * List exercises saga
 */
function* list() {
  try {
    const response = yield exercise.list();
    const {
      data: { availableExercises }
    } = response;

    yield put(exerciseSlice.actions.listSuccess(availableExercises));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.listError(message));
  }
}

/**
 * Find exercise saga
 */
function* find(action) {
  try {
    const id = action.payload.id || action.payload;
    const response = yield exercise.find(id);
    const {
      data: { exercise: currentExercise }
    } = response;

    yield put(exerciseSlice.actions.findSuccess(currentExercise));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.findError(message));
  }
}

/**
 * Create exercise saga (admin)
 */
function* create(action) {
  try {
    const snack = createSnack({
      key: 'exercise-create-notification-success',
      variant: 'success',
      message: i18n.t('exercise.create', { ns: 'notifications' })
    });

    const response = yield exercise.create(action.payload);
    yield put(exerciseSlice.actions.createSuccess(response?.data?.createExercise || null));
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    yield put(exerciseSlice.actions.list());
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.createError(message));
  }
}

/**
 * Update exercise saga (admin)
 */
function* update(action) {
  try {
    const snack = createSnack({
      key: 'exercise-update-notification-success',
      variant: 'success',
      message: i18n.t('exercise.update', { ns: 'notifications' })
    });

    yield exercise.update(action.payload);
    yield put(exerciseSlice.actions.updateSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    yield put(exerciseSlice.actions.list());
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.updateError(message));
  }
}

/**
 * Remove exercise saga (admin)
 */
function* remove(action) {
  try {
    const snack = createSnack({
      key: 'exercise-remove-notification-success',
      variant: 'success',
      message: i18n.t('exercise.delete', { ns: 'notifications' })
    });

    yield exercise.remove(action.payload);
    yield put(exerciseSlice.actions.removeSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    yield put(exerciseSlice.actions.list());
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.removeError(message));
  }
}

/**
 * Request exercise saga (trainer)
 */
function* requestExercise(action) {
  try {
    const snack = createSnack({
      key: 'exercise-request-notification-success',
      variant: 'success',
      message: i18n.t('exercise.request', { ns: 'notifications' })
    });

    yield exercise.requestExercise(action.payload);
    yield put(exerciseSlice.actions.requestExerciseSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.requestExerciseError(message));
  }
}

/**
 * List exercise requests saga (admin and trainer)
 */
function* listRequests(action) {
  try {
    // Get current user from auth state
    const auth = yield select((state) => state.auth);
    const userRole = auth?.user?.role;

    let response;
    let requests;

    // Use different query based on user role
    if (userRole === 'ADMINISTRATOR') {
      response = yield exercise.listRequests(action.payload);
      const { data: { exerciseRequests } } = response;
      requests = exerciseRequests.data || [];
    } else {
      // Trainer - use myExerciseRequests
      response = yield exercise.listMyRequests(action.payload);
      const { data: { myExerciseRequests } } = response;
      requests = myExerciseRequests || [];
    }

    yield put(exerciseSlice.actions.listRequestsSuccess(requests));
  } catch (ex) {
    console.error('=== EXERCISE REQUESTS ERROR ===');
    console.error('Error:', ex);
    console.error('Error message:', ex.message);
    console.error('Error response:', ex.graphQLErrors);
    console.error('===============================');
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.listRequestsError(message));
  }
}

/**
 * Approve exercise request saga (admin)
 */
function* approveRequest(action) {
  try {
    const snack = createSnack({
      key: 'exercise-approve-notification-success',
      variant: 'success',
      message: i18n.t('exercise.approve', { ns: 'notifications' })
    });

    yield exercise.approveRequest(action.payload);
    yield put(exerciseSlice.actions.approveRequestSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    yield put(exerciseSlice.actions.listRequests('PENDING'));
    yield put(exerciseSlice.actions.list());
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.approveRequestError(message));
  }
}

/**
 * Reject exercise request saga (admin)
 */
function* rejectRequest(action) {
  try {
    const snack = createSnack({
      key: 'exercise-reject-notification-success',
      variant: 'success',
      message: i18n.t('exercise.reject', { ns: 'notifications' })
    });

    const { id, reason, suggestedExerciseId } = action.payload;
    yield exercise.rejectRequest(id, reason, suggestedExerciseId);
    yield put(exerciseSlice.actions.rejectRequestSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    yield put(exerciseSlice.actions.listRequests('PENDING'));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.rejectRequestError(message));
  }
}

/**
 * Delete exercise request saga (admin)
 */
function* deleteRequest(action) {
  try {
    const snack = createSnack({
      key: 'exercise-delete-request-notification-success',
      variant: 'success',
      message: 'Solicitud eliminada correctamente'
    });

    const { id, currentStatus } = action.payload;
    yield exercise.deleteRequest(id);
    yield put(exerciseSlice.actions.deleteRequestSuccess());
    yield put(uiSlice.actions.enqueueSnackbar(snack));
    // Reload the current tab status
    yield put(exerciseSlice.actions.listRequests(currentStatus));
  } catch (ex) {
    const message = getUserFriendlyError(ex);
    yield put(exerciseSlice.actions.deleteRequestError(message));
  }
}

/**
 * Watch exercise
 */
export default function* watchExercise() {
  yield takeLatest(exerciseSlice.actions.list, list);
  yield takeLatest(exerciseSlice.actions.find, find);
  yield takeLatest(exerciseSlice.actions.create, create);
  yield takeLatest(exerciseSlice.actions.update, update);
  yield takeLatest(exerciseSlice.actions.remove, remove);
  yield takeLatest(exerciseSlice.actions.requestExercise, requestExercise);
  yield takeLatest(exerciseSlice.actions.listRequests, listRequests);
  yield takeLatest(exerciseSlice.actions.approveRequest, approveRequest);
  yield takeLatest(exerciseSlice.actions.rejectRequest, rejectRequest);
  yield takeLatest(exerciseSlice.actions.deleteRequest, deleteRequest);
}
